
import './style.scss';
import React, { useCallback } from 'react';
import { Box, Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography, Backdrop } from "@mui/material";
import { Close } from '@mui/icons-material';


export interface IDefaultDialogProps {
    open: boolean;
    handleClose: () => void;
}


export interface IAddEditDialogProps<Type> extends IDefaultDialogProps {
    data: Type | undefined;
    handleListRefresh?: () => void;
}

export interface IRemoveDialogProps<Type> extends IAddEditDialogProps<Type> { }

export interface IDialogProps extends IDefaultDialogProps {
    showCloseButton?: boolean;
    onCloseEventEnabled?: boolean;
    title?: string;
    fullWidth?: boolean;
    fullHeight?: boolean;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    headerActionsLeft?: JSX.Element | JSX.Element[] | null;
    headerActionsRight?: JSX.Element | JSX.Element[] | null;
    dialogActions?: JSX.Element | JSX.Element[] | null;
    className?: string;
    onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
    absolute?: boolean;
    afterForm?: JSX.Element | JSX.Element[] | null;
    dividers?: boolean;
    [rest: string]: any;
}


const Dialog: React.FunctionComponent<IDialogProps> = ({
    open,
    handleClose,
    showCloseButton = true,
    onCloseEventEnabled = false,
    title = undefined,
    fullWidth = false,
    fullHeight = false,
    maxWidth = 'xs',
    headerActionsLeft = null,
    headerActionsRight = null,
    dialogActions = null,
    className = '',
    onSubmit = undefined,
    absolute = false,
    afterForm = null,
    dividers = false,
    ...rest
}) => {
    
    const constructDialogActions = useCallback(() => {
        if (dialogActions) {
            return (
                <DialogActions className='dialogactions-default'>
                    {dialogActions}
                </DialogActions>
            );
        }

        return null;
        // eslint-disable-next-line
    }, [dialogActions]);


    const constructDialogForm = useCallback((children: JSX.Element) => {
        if (onSubmit !== undefined) {
            return <Box component='form' onSubmit={onSubmit} className='dialogform-default'>
                {children}
            </Box>;
        }

        return <Box className='dialogform-default'>
            {children}
        </Box>;
    }, [onSubmit]);


    if (absolute) {
        return <Backdrop open={open} className='absolutedialog-backdrop' onClick={onCloseEventEnabled ? handleClose : undefined}>
            <Box className={`dialog-default ${className} ${open ? '' : 'display-none'}`}>
                {
                    constructDialogForm(<>
                        <DialogTitle className='dialogtitle-default'>
                            <Grid container className='alignitems-center'>
                                {
                                    headerActionsLeft && <Grid item className='display-flex justifycontent-flexend' paddingRight={1}>
                                        {headerActionsLeft}
                                    </Grid>
                                }

                                <Grid item>
                                    {
                                        title && <Typography variant='subtitle1' className='dialogtitle-text'>{title}</Typography>
                                    }
                                </Grid>

                                <Grid item flexGrow={2} className='display-flex justifycontent-flexend'>
                                    {headerActionsRight}
                                    
                                    {
                                        showCloseButton && <Box>
                                            <IconButton size='small' onClick={handleClose}>
                                                <Close fontSize='inherit' />
                                            </IconButton>
                                        </Box>
                                    }
                                </Grid>
                            </Grid>
                        </DialogTitle>
                        
                        <DialogContent className='dialogcontent-default' dividers={dividers}>
                            {rest.children}
                        </DialogContent>
                        
                        {
                            constructDialogActions()
                        }
                    </>)
                }

                {afterForm}
            </Box>
        </Backdrop>
    }

    return (
        <MuiDialog
            open={open}
            fullWidth={fullWidth}
            maxWidth={maxWidth}
            onClose={onCloseEventEnabled ? handleClose : undefined}
            PaperProps={{
                className: `dialog-default ${className} ${fullHeight ? 'full-height' : ''}`
            }}
        >
            {
                constructDialogForm(<>
                    <DialogTitle className='dialogtitle-default'>
                        <Grid container className='alignitems-center'>
                            {
                                headerActionsLeft && <Grid item className='display-flex justifycontent-flexend' paddingRight={1}>
                                    {headerActionsLeft}
                                </Grid>
                            }

                            <Grid item>
                                {
                                    title && <Typography variant='subtitle1' className='dialogtitle-text'>{title}</Typography>
                                }
                            </Grid>

                            <Grid item flexGrow={2} className='display-flex justifycontent-flexend'>
                                {headerActionsRight}
                                
                                {
                                    showCloseButton && <Box>
                                        <IconButton size='small' onClick={handleClose}>
                                            <Close fontSize='inherit' />
                                        </IconButton>
                                    </Box>
                                }
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    
                    <DialogContent className='dialogcontent-default' dividers={dividers || fullHeight}>
                        {rest.children}
                    </DialogContent>
                    
                    {
                        constructDialogActions()
                    }
                </>)
            }

            {afterForm}
        </MuiDialog>
    );
}

export default Dialog;






export interface IDialogSectionTitleProps {
    title: string;
}

export const DialogSectionTitle: React.FunctionComponent<IDialogSectionTitleProps> = ({
    title
}) => {
    return (
        <Box marginBottom={2}>
            <Typography variant="subtitle1" component='span' className='dialog-sectiontitle'>
                {title}
            </Typography>
        </Box>
    );
}
